import React, { useCallback, useEffect, useState, useRef } from "react";
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Delete, Edit, Trash2, Upload, FileSpreadsheet, X, Save, Clock, PowerOff } from "lucide-react";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Stack,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    IconButton,
    Typography,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tabs,
    Tab,
    Box,
    Card,
    CardContent,
    CardActions,
    Divider,
} from "@mui/material";
import { getRequest, postRequest, deleteRequest, patchRequest } from "../../ApiFunction";
import API from "../../Api";
import { toast } from "react-toastify";
import { getESSErrorMessage, isESSSuccess } from "../../utils/essErrorHandler";

// Local Storage Keys
const DRAFT_STORAGE_KEY = 'product_drafts';
const LOCAL_PRODUCTS_KEY = 'local_products';

const Product = () => {
    const [open, setOpen] = React.useState(false);
    const [scroll, setScroll] = React.useState('paper');
    const [editMode, setEditMode] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Tab State
    const [activeTab, setActiveTab] = useState(0);
    
    // Draft States - Initialize from localStorage
    const [drafts, setDrafts] = useState(() => {
        try {
            const savedDrafts = localStorage.getItem(DRAFT_STORAGE_KEY);
            return savedDrafts ? JSON.parse(savedDrafts) : [];
        } catch {
            return [];
        }
    });
    const [editingDraftId, setEditingDraftId] = useState(null);
    
    // Local Products (stored locally) - Initialize from localStorage
    const [localProducts, setLocalProducts] = useState(() => {
        try {
            const saved = localStorage.getItem(LOCAL_PRODUCTS_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    
    // Decommission States
    const [decommissionDialogOpen, setDecommissionDialogOpen] = useState(false);
    const [selectedForDecommission, setSelectedForDecommission] = useState([]);
    const [decommissioning, setDecommissioning] = useState(false);
    
    // CSV Upload States
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [csvPreviewData, setCsvPreviewData] = useState([]);
    const [csvErrors, setCsvErrors] = useState([]);
    const [csvFileName, setCsvFileName] = useState("");
    const csvInputRef = useRef(null);
    
    const [form, setForm] = useState({
        fspCode: "",
        fspName: "",
        productCode: "",
        productName: "",
        minTenure: "",
        maxTenure: "",
        interestRate: "",
        processingFee: "",
        insurance: "",
        minAmount: "",
        maxAmount: "",
        repaymentType: "",
        insuranceType: "",
        productDescription: "",
        termsCondition: [],
        forExecutive: false,
        shariaFacility: false,
        deductionCode: "",
    });
    const [loanProducts, setLoanProducts] = useState([])
    const [errors, setErrors] = useState({});



    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));

        // Inline validation
        let error = "";
        switch (field) {
            case "fspCode":
                if (!value) error = "FSP Code is required";
                else if (value.length > 10) error = "Max 10 characters";
                break;
            case "fspName":
                if (!value) error = "FSP Name is required";
                else if (value.length > 100) error = "Max 100 characters";
                break;
            case "productCode":
                if (!value) error = "Product Code is required";
                else if (value.length > 8) error = "Max 8 characters";
                break;
            case "productName":
                if (!value) error = "Product Name is required";
                else if (value.length > 255) error = "Max 255 characters";
                break;
            case "deductionCode":
                if (!value) error = "Deduction Code is required";
                else if (value.length > 10) error = "Max 10 characters";
                break;
            case "minTenure":
            case "maxTenure":
                if (!value) error = "This field is required";
                else if (+value > 999) error = "Max 3 digits";
                break;
            case "interestRate":
            case "processingFee":
            case "insurance":
                if (field !== "processingFee" && !value) error = "This field is required";
                else if (+value > 999.99) error = "Max 3,2 digits";
                break;
            case "minAmount":
            case "maxAmount":
                if (!value) error = "This field is required";
                else if (+value > 99999999999999999999999999999999999999)
                    error = "Max 38,2 digits";
                break;
            case "insuranceType":
                if (!value) error = "Insurance Type is required";
                else if (value.length > 50) error = "Max 50 characters";
                break;
            case "termsCondition":
                if (!form.termsCondition.length) error = "At least one term is required";
                else {
                    form.termsCondition.forEach((t, i) => {
                        if (!t.termNumber || !t.description || !t.effectiveDate) {
                            error = `Term ${i + 1} is incomplete`;
                        }
                    });
                }
                break;
            default:
                return
        }
        setErrors((prev) => ({ ...prev, [field]: error }));
    };

 
    const createProduct = async (formData) => {
        const toastId = toast.loading("Creating product...");

        try {
            const result = await postRequest(API.CREATE_PRODUCT, formData);

            // Handle ESS response format
            if (result.data && result.data.Document) {
                const essData = result.data.Document.Data;
                const messageDetails = essData.MessageDetails;
                const responseCode = messageDetails.ResponseCode;
                const description = messageDetails.Description;

                const userMessage = getESSErrorMessage(responseCode, description);

                if (!isESSSuccess(responseCode)) {
                    toast.error(userMessage, {
                        id: toastId,
                        autoClose: 5000,
                        position: "top-right"
                    });
                    return { success: false, message: userMessage };
                }

                // Success
                toast.success("Product created successfully!", {
                    id: toastId,
                    autoClose: 3000,
                    position: "top-right"
                });
                return { success: true, data: result.data };

            } else {
                // Handle non-ESS format (your backend errors)
                const { success, message } = result.data;
                if (!success) {
                    toast.error(message, {
                        id: toastId,
                        autoClose: 3000,
                        position: "top-right"
                    });
                    return { success: false, message };
                }

                toast.success("Product created successfully!", {
                    id: toastId,
                    autoClose: 3000,
                    position: "top-right"
                });
                return { success: true, data: result.data };
            }

        } catch (err) {
            console.error('API Error:', err);

            let errorMessage = 'An unexpected error occurred';

            if (err.response) {
                // Server responded with error
                const errorData = err.response.data;

                if (errorData.Document) {
                    // ESS format error
                    const essError = errorData.Document.Data.MessageDetails;
                    errorMessage = getESSErrorMessage(essError.ResponseCode, essError.Description);
                } else if (errorData.message) {
                    // Your backend error
                    errorMessage = errorData.message;
                } else if (errorData.responseCode) {
                    // Alternative error format
                    errorMessage = errorData.description || `Error: ${errorData.responseCode}`;
                }

            } else if (err.request) {
                // Network / connection reset / timeout
                if (err.code === 'ECONNABORTED') {
                    errorMessage = 'Product service timeout. The server did not respond in time.';
                } else if (err.code === 'ERR_NETWORK') {
                    errorMessage = 'Product service connection was reset. Please try again. If it persists, contact backend support for /auth/product-create.';
                } else {
                    errorMessage = 'Unable to connect to server. Please check your internet connection.';
                }
            } else {
                // Other errors
                errorMessage = err.message;
            }

            toast.error(errorMessage, {
                id: toastId,
                autoClose: 5000,
                position: "top-right"
            });

            return { success: false, message: errorMessage };
        }
    };





   const handleSaveProduct = async () => {
        // Check all required fields
        const requiredFields = [
            "fspCode",
            "fspName",
            "productCode",
            "productName",
            "minTenure",
            "maxTenure",
            "interestRate",
            "insurance",
            "minAmount",
            "maxAmount",
            "deductionCode",
            "insuranceType",
            "termsCondition",
        ];
        const newErrors = {};
        requiredFields.forEach((field) => {
            if (!form[field]) {
                newErrors[field] = "This field is required";
            }
        });
        if (!form.termsCondition || form.termsCondition.length === 0) {
            newErrors["termsCondition"] = "At least one term is required";
        }
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            setSaving(true);
            let result;
            if (editMode) {
                result = await updateProduct(form);
            } else {
                result = await createProduct(form);
                // Save locally if successful
                if (result?.success) {
                    saveProductLocally(form);
                    handleClose();
                    fetchProducts();
                }
            }
            setSaving(false);
        }
    };

    // Update Product
    const updateProduct = async (formData) => {
        const toastId = toast.loading("Updating product...");
        setSaving(true);

        try {
            const result = await patchRequest('/auth/product-update', {
                id: selectedProduct?.id,
                ...formData
            });

            if (result.data.success) {
                toast.success("Product updated successfully!", { id: toastId });
                handleClose();
                fetchProducts();
                return { success: true };
            } else {
                toast.error(result.data.message || "Failed to update product", { id: toastId });
                return { success: false };
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error updating product", { id: toastId });
            return { success: false };
        } finally {
            setSaving(false);
        }
    };

    // Delete Product
    const handleDeleteProduct = async () => {
        const toastId = toast.loading("Deleting product...");
        setSaving(true);

        try {
            const result = await deleteRequest('/auth/product-delete', { 
                id: selectedProduct?.id 
            });

            if (result.data.success) {
                toast.success("Product deleted successfully!", { id: toastId });
                setDeleteDialogOpen(false);
                setSelectedProduct(null);
                fetchProducts();
            } else {
                toast.error(result.data.message || "Failed to delete product", { id: toastId });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error deleting product", { id: toastId });
        } finally {
            setSaving(false);
        }
    };

    // Open Edit Dialog
    const openEditDialog = (product) => {
        setSelectedProduct(product);
        setEditMode(true);
        setForm({
            fspCode: product.fspCode || "",
            fspName: product.fspName || "",
            productCode: product.productCode || product.shortName || "",
            productName: product.name || "",
            minTenure: product.minTenure || "",
            maxTenure: product.maxTenure || "",
            interestRate: product.interestRate || "",
            processingFee: product.processingFee || "",
            insurance: product.insurance || "",
            minAmount: product.minPrincipal || "",
            maxAmount: product.maxPrincipal || "",
            repaymentType: product.repaymentType || "",
            insuranceType: product.insuranceType || "",
            productDescription: product.productDescription || "",
            termsCondition: product.termsCondition || [],
            forExecutive: product.forExecutive || false,
            shariaFacility: product.shariaFacility || false,
            deductionCode: product.deductionCode || "",
        });
        setOpen(true);
        setScroll('paper');
    };

    // Open Delete Dialog
    const openDeleteDialog = (product) => {
        setSelectedProduct(product);
        setDeleteDialogOpen(true);
    };


    const fetchProducts = useCallback(async (event) => {
        try {
            const result = await getRequest(API.ALL_PRODUCTS);

            const { status, data, message } = result.data;

            if (!status) {
                toast.error(message);
                return;
            }
            const newData = data.map((p) => ({
                id: p.id,
                name: p.name,
                shortName: p.shortName,
                currency: p.currency.code,
                principal: p.principal,
                minPrincipal: p.minPrincipal,
                maxPrincipal: p.maxPrincipal,
                interestRate: p.annualInterestRate,
                repayments: p.numberOfRepayments,
                repaymentFrequency: `${p.repaymentEvery} ${p.repaymentFrequencyType.value}`,
                amortization: p.amortizationType.value,
                interestType: p.interestType.value,
                startDate: new Date(p.startDate[0], p.startDate[1] - 1, p.startDate[2]).toISOString(),
                status: p.status,
            }));
            setLoanProducts(newData || [])
        } catch (err) {
            toast.error(err.message);
            console.error(err.message);
        }
    }, []);



    const handleAddTerm = () => {
        setForm((prev) => ({
            ...prev,
            termsCondition: [
                ...prev.termsCondition,
                { termNumber: "", description: "", effectiveDate: "" },
            ],
        }));
    };

    const handleRemoveTerm = (index) => {
        setForm((prev) => ({
            ...prev,
            termsCondition: prev.termsCondition.filter((_, i) => i !== index),
        }));
    };

    const handleTermChange = (index, field, value) => {
        // Validate description max length (255 chars per ESS spec)
        if (field === 'description' && value.length > 255) {
            toast.warning(`Description cannot exceed 255 characters (currently ${value.length})`);
            return;
        }
        // Validate term number max length (20 chars per ESS spec)
        if (field === 'termNumber' && value.length > 20) {
            toast.warning(`Term Number cannot exceed 20 characters`);
            return;
        }
        
        const updatedTerms = [...form.termsCondition];
        updatedTerms[index][field] = value;
        setForm((prev) => ({ ...prev, termsCondition: updatedTerms }));
    };

    // CSV Upload Handlers
    const handleCsvUploadClick = () => {
        csvInputRef.current?.click();
    };

    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
            return { data: [], errors: ['CSV file must have a header row and at least one data row'] };
        }

        const headerLine = lines[0];
        const headers = headerLine.split(',').map(h => h.trim().replace(/"/g, ''));
        
        // Check for required headers (case-insensitive)
        const headerLower = headers.map(h => h.toLowerCase());
        const requiredHeaders = ['termsconditionumber', 'description', 'tceffectivedate'];
        const alternativeHeaders = ['termnumber', 'term_number', 'termscondition_number'];
        
        // Find column indices
        let termNumberIdx = headerLower.findIndex(h => 
            requiredHeaders[0].includes(h.replace(/[^a-z]/g, '')) || 
            alternativeHeaders.some(alt => h.replace(/[^a-z]/g, '').includes(alt.replace(/[^a-z]/g, ''))) ||
            h.replace(/[^a-z]/g, '') === 'termnumber'
        );
        let descriptionIdx = headerLower.findIndex(h => h.replace(/[^a-z]/g, '').includes('description'));
        let effectiveDateIdx = headerLower.findIndex(h => 
            h.replace(/[^a-z]/g, '').includes('effectivedate') || 
            h.replace(/[^a-z]/g, '').includes('tceffectivedate')
        );

        // Fallback to position-based if headers not found
        if (termNumberIdx === -1) termNumberIdx = 0;
        if (descriptionIdx === -1) descriptionIdx = 1;
        if (effectiveDateIdx === -1) effectiveDateIdx = 2;

        const data = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // Handle CSV with quoted fields
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let j = 0; j < line.length; j++) {
                const char = line[j];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    values.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            values.push(current.trim());

            const termNumber = values[termNumberIdx]?.replace(/"/g, '') || '';
            const description = values[descriptionIdx]?.replace(/"/g, '') || '';
            let effectiveDate = values[effectiveDateIdx]?.replace(/"/g, '') || '';

            // Validate and format date
            if (effectiveDate) {
                // Handle various date formats
                const datePatterns = [
                    { regex: /^(\d{4})-(\d{2})-(\d{2})$/, format: (m) => `${m[1]}-${m[2]}-${m[3]}` }, // YYYY-MM-DD
                    { regex: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, format: (m) => `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}` }, // YYYY-M-D
                    { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, format: (m) => `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}` }, // D/M/YYYY or DD/MM/YYYY
                    { regex: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, format: (m) => `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}` }, // D-M-YYYY or DD-MM-YYYY
                    { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, format: (m) => `20${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}` }, // D/M/YY
                ];
                
                let validDate = false;
                for (const pattern of datePatterns) {
                    const match = effectiveDate.match(pattern.regex);
                    if (match) {
                        effectiveDate = pattern.format(match);
                        validDate = true;
                        break;
                    }
                }
                
                if (!validDate && effectiveDate) {
                    errors.push(`Row ${i}: Invalid date format "${effectiveDate}". Use YYYY-MM-DD.`);
                }
            }

            // Validate required fields
            if (!termNumber) {
                errors.push(`Row ${i}: Term Number is required`);
            } else if (termNumber.length > 20) {
                errors.push(`Row ${i}: Term Number exceeds 20 characters (${termNumber.length})`);
            }
            if (!description) {
                errors.push(`Row ${i}: Description is required`);
            } else if (description.length > 255) {
                errors.push(`Row ${i}: Description exceeds 255 characters (${description.length})`);
            }
            if (!effectiveDate) {
                errors.push(`Row ${i}: Effective Date is required`);
            }

            if (termNumber || description || effectiveDate) {
                data.push({
                    termNumber,
                    description,
                    effectiveDate,
                });
            }
        }

        return { data, errors };
    };

    const handleCsvFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }

        setCsvFileName(file.name);
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result;
            const { data, errors } = parseCSV(text);
            
            setCsvPreviewData(data);
            setCsvErrors(errors);
            setCsvDialogOpen(true);
        };
        reader.onerror = () => {
            toast.error('Error reading file');
        };
        reader.readAsText(file);

        // Reset input so same file can be selected again
        event.target.value = '';
    };

    const handleCsvImportConfirm = () => {
        if (csvPreviewData.length === 0) {
            toast.error('No valid data to import');
            return;
        }

        // Append to existing terms or replace
        setForm((prev) => ({
            ...prev,
            termsCondition: [...prev.termsCondition, ...csvPreviewData],
        }));

        toast.success(`Imported ${csvPreviewData.length} terms successfully`);
        setCsvDialogOpen(false);
        setCsvPreviewData([]);
        setCsvErrors([]);
        setCsvFileName("");
    };

    const handleCsvReplace = () => {
        if (csvPreviewData.length === 0) {
            toast.error('No valid data to import');
            return;
        }

        // Replace all existing terms
        setForm((prev) => ({
            ...prev,
            termsCondition: csvPreviewData,
        }));

        toast.success(`Replaced with ${csvPreviewData.length} terms from CSV`);
        setCsvDialogOpen(false);
        setCsvPreviewData([]);
        setCsvErrors([]);
        setCsvFileName("");
    };

    const handleCsvDialogClose = () => {
        setCsvDialogOpen(false);
        setCsvPreviewData([]);
        setCsvErrors([]);
        setCsvFileName("");
    };

    const downloadCsvTemplate = () => {
        const template = `TermsConditionNumber,Description,TCEffectiveDate
123456,Payment Must be Made in Full,2024-02-22
123457,Loan must be paid within time,2024-02-22
123458,Interest is calculated monthly,2024-03-01`;
        
        const blob = new Blob([template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'terms_conditions_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ==================== DRAFT FUNCTIONS ====================
    
    // Save drafts to localStorage
    const saveDraftsToStorage = (draftsData) => {
        try {
            localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftsData));
        } catch (err) {
            console.error('Error saving drafts:', err);
        }
    };

    // Save current form as draft
    const handleSaveAsDraft = () => {
        const draftId = editingDraftId || `draft_${Date.now()}`;
        const draftData = {
            id: draftId,
            ...form,
            savedAt: new Date().toISOString(),
            productName: form.productName || 'Untitled Product',
        };

        let updatedDrafts;
        if (editingDraftId) {
            // Update existing draft
            updatedDrafts = drafts.map(d => d.id === editingDraftId ? draftData : d);
            toast.success('Draft updated successfully');
        } else {
            // Add new draft
            updatedDrafts = [...drafts, draftData];
            toast.success('Draft saved! You can continue later.');
        }

        setDrafts(updatedDrafts);
        saveDraftsToStorage(updatedDrafts);
        handleClose();
    };

    // Load a draft into the form
    const handleLoadDraft = (draft) => {
        setEditingDraftId(draft.id);
        setForm({
            fspCode: draft.fspCode || "",
            fspName: draft.fspName || "",
            productCode: draft.productCode || "",
            productName: draft.productName || "",
            minTenure: draft.minTenure || "",
            maxTenure: draft.maxTenure || "",
            interestRate: draft.interestRate || "",
            processingFee: draft.processingFee || "",
            insurance: draft.insurance || "",
            minAmount: draft.minAmount || "",
            maxAmount: draft.maxAmount || "",
            repaymentType: draft.repaymentType || "",
            insuranceType: draft.insuranceType || "",
            productDescription: draft.productDescription || "",
            termsCondition: draft.termsCondition || [],
            forExecutive: draft.forExecutive || false,
            shariaFacility: draft.shariaFacility || false,
            deductionCode: draft.deductionCode || "",
        });
        setEditMode(false);
        setOpen(true);
        setScroll('paper');
    };

    // Delete a draft
    const handleDeleteDraft = (draftId) => {
        const updatedDrafts = drafts.filter(d => d.id !== draftId);
        setDrafts(updatedDrafts);
        saveDraftsToStorage(updatedDrafts);
        toast.info('Draft deleted');
    };

    // ==================== LOCAL PRODUCTS FUNCTIONS ====================

    // Save product locally after successful creation
    const saveProductLocally = (productData) => {
        const localProduct = {
            id: `local_${Date.now()}`,
            ...productData,
            createdAt: new Date().toISOString(),
            status: 'active',
            sentToESS: true,
        };

        const updated = [...localProducts, localProduct];
        setLocalProducts(updated);
        localStorage.setItem(LOCAL_PRODUCTS_KEY, JSON.stringify(updated));

        // Remove from drafts if it was a draft
        if (editingDraftId) {
            const updatedDrafts = drafts.filter(d => d.id !== editingDraftId);
            setDrafts(updatedDrafts);
            saveDraftsToStorage(updatedDrafts);
            setEditingDraftId(null);
        }
    };

    // ==================== PRODUCT DECOMMISSION FUNCTIONS ====================
    
    const handleOpenDecommissionDialog = () => {
        setSelectedForDecommission([]);
        setDecommissionDialogOpen(true);
    };

    const handleToggleDecommissionSelect = (productCode) => {
        setSelectedForDecommission(prev => {
            if (prev.includes(productCode)) {
                return prev.filter(c => c !== productCode);
            }
            return [...prev, productCode];
        });
    };

    const handleDecommissionProducts = async () => {
        if (selectedForDecommission.length === 0) {
            toast.warning('Please select at least one product to decommission');
            return;
        }

        setDecommissioning(true);
        const toastId = toast.loading('Decommissioning products...');

        try {
            // Send PRODUCT_DECOMMISSION message to ESS
            const payload = {
                products: selectedForDecommission.map(code => ({ productCode: code }))
            };

            const result = await postRequest(API.MESSAGE_PRODUCT_DECOMMISSION, payload);

            // Handle ESS response
            if (result.data && result.data.Document) {
                const essData = result.data.Document.Data;
                const messageDetails = essData.MessageDetails;
                const responseCode = messageDetails?.ResponseCode;
                const description = messageDetails?.Description;

                const userMessage = getESSErrorMessage(responseCode, description);

                if (!isESSSuccess(responseCode)) {
                    toast.error(userMessage, { id: toastId });
                    return;
                }

                // Update local products status
                const updatedLocal = localProducts.map(p => {
                    if (selectedForDecommission.includes(p.productCode)) {
                        return { ...p, status: 'decommissioned', decommissionedAt: new Date().toISOString() };
                    }
                    return p;
                });
                setLocalProducts(updatedLocal);
                localStorage.setItem(LOCAL_PRODUCTS_KEY, JSON.stringify(updatedLocal));

                toast.success(`Successfully decommissioned ${selectedForDecommission.length} product(s)`, { id: toastId });
                setDecommissionDialogOpen(false);
                setSelectedForDecommission([]);
            } else if (result.data?.success) {
                // Non-ESS format success
                const updatedLocal = localProducts.map(p => {
                    if (selectedForDecommission.includes(p.productCode)) {
                        return { ...p, status: 'decommissioned', decommissionedAt: new Date().toISOString() };
                    }
                    return p;
                });
                setLocalProducts(updatedLocal);
                localStorage.setItem(LOCAL_PRODUCTS_KEY, JSON.stringify(updatedLocal));

                toast.success(`Successfully decommissioned ${selectedForDecommission.length} product(s)`, { id: toastId });
                setDecommissionDialogOpen(false);
                setSelectedForDecommission([]);
            } else {
                toast.error(result.data?.message || 'Failed to decommission products', { id: toastId });
            }
        } catch (err) {
            console.error('Decommission error:', err);
            toast.error(err.response?.data?.message || 'Error decommissioning products', { id: toastId });
        } finally {
            setDecommissioning(false);
        }
    };

    // Get all active products (for decommission selection)
    const getActiveProducts = () => {
        const fromLocal = localProducts.filter(p => p.status === 'active');
        const fromApi = loanProducts.filter(p => p.status === 'active' || p.status === 'loanProduct.active');
        
        // Combine and dedupe by productCode
        const combined = [...fromLocal];
        fromApi.forEach(p => {
            if (!combined.find(lp => lp.productCode === p.shortName)) {
                combined.push({
                    ...p,
                    productCode: p.shortName,
                    productName: p.name,
                });
            }
        });
        
        return combined;
    };



    const handleClickOpen = (scrollType) => () => {
        setEditMode(false);
        setSelectedProduct(null);
        setEditingDraftId(null);
        setForm({
            fspCode: "",
            fspName: "",
            productCode: "",
            productName: "",
            minTenure: "",
            maxTenure: "",
            interestRate: "",
            processingFee: "",
            insurance: "",
            minAmount: "",
            maxAmount: "",
            repaymentType: "",
            insuranceType: "",
            productDescription: "",
            termsCondition: [],
            forExecutive: false,
            shariaFacility: false,
            deductionCode: "",
        });
        setErrors({});
        setOpen(true);
        setScroll(scrollType);
    };

    const handleClose = () => {
        setOpen(false);
        setEditMode(false);
        setEditingDraftId(null);
        setSelectedProduct(null);
        setErrors({});
    };

    const descriptionElementRef = React.useRef(null);
    React.useEffect(() => {
        if (open) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [open]);

    const columns = [
        { field: "id", headerName: "ID", width: 70 },
        { field: "name", headerName: "Product Name", width: 200 },
        { field: "shortName", headerName: "Short Name", width: 120 },
        { field: "currency", headerName: "Currency", width: 100 },
        { field: "principal", headerName: "Principal", width: 140 },
        { field: "minPrincipal", headerName: "Min Principal", width: 140 },
        { field: "maxPrincipal", headerName: "Max Principal", width: 160 },
        { field: "interestRate", headerName: "Interest Rate (%)", width: 160 },
        { field: "repayments", headerName: "No. of Repayments", width: 180 },
        { field: "repaymentFrequency", headerName: "Repayment Frequency", width: 200 },
        { field: "amortization", headerName: "Amortization", width: 180 },
        { field: "interestType", headerName: "Interest Type", width: 180 },
        {
            field: "startDate",
            headerName: "Start Date",
            width: 160,
            valueGetter: (params) => new Date(params.row.startDate).toLocaleDateString(),
        },
        { field: "status", headerName: "Status", width: 180 },
        {
            field: "action",
            headerName: "Action",
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <div className="d-flex justify-content-center gap-2 align-items-center">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(params.row)}
                        title="Edit Product"
                    >
                        <Edit size={18} />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(params.row)}
                        title="Delete Product"
                    >
                        <Delete size={18} />
                    </IconButton>
                </div>
            ),
        },
    ];



    const paginationModel = { page: 0, pageSize: 5 };

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts])




    return (
        <div className="container">
            <div className="row">
                <div className="col-xs-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1">Product Management</h5>
                            <p className="text-muted mb-4">
                                Manage product catalog, update product details, and control availability.
                            </p>
                        </div>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<PowerOff size={16} />}
                                onClick={handleOpenDecommissionDialog}
                                sx={{ textTransform: 'none' }}
                            >
                                Decommission
                            </Button>
                            <button className="custom-button" onClick={handleClickOpen('paper')}>Add Product</button>
                        </Stack>
                    </div>

                    {/* Tabs for Products, Drafts, Local Products */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={`Products (${loanProducts.length})`} />
                            <Tab label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Clock size={16} />
                                    <span>Drafts ({drafts.length})</span>
                                </Stack>
                            } />
                            <Tab label={`Local Products (${localProducts.length})`} />
                        </Tabs>
                    </Box>

                    {/* Tab Panel: Products from API */}
                    {activeTab === 0 && (
                        <Paper className="custom-paper">
                            <DataGrid
                                rows={loanProducts}
                                columns={columns}
                                initialState={{ pagination: { paginationModel } }}
                                pageSizeOptions={[5, 10]}
                                sx={{ border: 0 }}
                            />
                        </Paper>
                    )}

                    {/* Tab Panel: Drafts */}
                    {activeTab === 1 && (
                        <Box>
                            {drafts.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No saved drafts. Use "Save as Draft" when adding a product to save your progress.
                                </Alert>
                            ) : (
                                <Stack spacing={2}>
                                    {drafts.map((draft) => (
                                        <Card key={draft.id} variant="outlined">
                                            <CardContent>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                    <div>
                                                        <Typography variant="h6">
                                                            {draft.productName || 'Untitled Product'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Product Code: {draft.productCode || 'Not set'} | 
                                                            FSP: {draft.fspCode || 'Not set'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Saved: {new Date(draft.savedAt).toLocaleString()}
                                                        </Typography>
                                                    </div>
                                                    <Chip 
                                                        label="Draft" 
                                                        size="small" 
                                                        color="warning" 
                                                        variant="outlined"
                                                    />
                                                </Stack>
                                                {draft.termsCondition?.length > 0 && (
                                                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                        {draft.termsCondition.length} Terms & Conditions configured
                                                    </Typography>
                                                )}
                                            </CardContent>
                                            <Divider />
                                            <CardActions>
                                                <Button 
                                                    size="small" 
                                                    startIcon={<Edit size={14} />}
                                                    onClick={() => handleLoadDraft(draft)}
                                                >
                                                    Continue Editing
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    color="error"
                                                    startIcon={<Trash2 size={14} />}
                                                    onClick={() => handleDeleteDraft(draft.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {/* Tab Panel: Local Products */}
                    {activeTab === 2 && (
                        <Box>
                            {localProducts.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No locally stored products. Products created through this admin panel will appear here.
                                </Alert>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Product Code</TableCell>
                                                <TableCell>Product Name</TableCell>
                                                <TableCell>FSP Code</TableCell>
                                                <TableCell>Interest Rate</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Created</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {localProducts.map((product) => (
                                                <TableRow key={product.id} hover>
                                                    <TableCell>{product.productCode}</TableCell>
                                                    <TableCell>{product.productName}</TableCell>
                                                    <TableCell>{product.fspCode}</TableCell>
                                                    <TableCell>{product.interestRate}%</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={product.status} 
                                                            size="small"
                                                            color={product.status === 'active' ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(product.createdAt).toLocaleDateString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </div>
            </div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                scroll={scroll}
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
            >
                <DialogTitle id="scroll-dialog-title">{editMode ? 'Edit Product' : 'Add Product'}</DialogTitle>
                <DialogContent dividers={scroll === 'paper'}>
                    <DialogContentText
                        id="scroll-dialog-description"
                        ref={descriptionElementRef}
                        tabIndex={-1}
                    >
                        <Stack spacing={2}>
                            {/* FSP Code */}
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="FSP Code"
                                    fullWidth
                                    size="small"
                                    value={form.fspCode}
                                    onChange={(e) => handleFormChange("fspCode", e.target.value)}
                                    error={!!errors.fspCode}
                                    helperText={errors.fspCode}
                                />
                                {/* FSP Name */}
                                <TextField
                                    label="FSP Name"
                                    fullWidth
                                    size="small"
                                    value={form.fspName}
                                    onChange={(e) => handleFormChange("fspName", e.target.value)}
                                    error={!!errors.fspName}
                                    helperText={errors.fspName}
                                />
                            </Stack>
                            {/* Product Code */}
                            <TextField
                                label="Product Code"
                                fullWidth
                                size="small"
                                value={form.productCode}
                                onChange={(e) => handleFormChange("productCode", e.target.value)}
                                error={!!errors.productCode}
                                helperText={errors.productCode}
                            />
                            {/* Product Name */}
                            <TextField
                                label="Product Name"
                                fullWidth
                                size="small"
                                value={form.productName}
                                onChange={(e) => handleFormChange("productName", e.target.value)}
                                error={!!errors.productName}
                                helperText={errors.productName}
                            />
                            {/* Deduction Code */}
                            <TextField
                                label="Deduction Code"
                                fullWidth
                                size="small"
                                value={form.deductionCode}
                                onChange={(e) => handleFormChange("deductionCode", e.target.value)}
                                error={!!errors.deductionCode}
                                helperText={errors.deductionCode}
                            />
                            {/* Product Description */}
                            <TextField
                                label="Product Description"
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                value={form.productDescription}
                                onChange={(e) => handleFormChange("productDescription", e.target.value)}
                            />
                            {/* Tenure */}
                            <TextField
                                label="Min Tenure"
                                type="number"
                                fullWidth
                                size="small"
                                value={form.minTenure}
                                onChange={(e) => handleFormChange("minTenure", e.target.value)}
                                error={!!errors.minTenure}
                                helperText={errors.minTenure}
                            />
                            <TextField
                                label="Max Tenure"
                                type="number"
                                fullWidth
                                size="small"
                                value={form.maxTenure}
                                onChange={(e) => handleFormChange("maxTenure", e.target.value)}
                                error={!!errors.maxTenure}
                                helperText={errors.maxTenure}
                            />
                            {/* Interest, Processing, Insurance */}
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Interest Rate (%)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.interestRate}
                                    onChange={(e) => handleFormChange("interestRate", e.target.value)}
                                    error={!!errors.interestRate}
                                    helperText={errors.interestRate}
                                />
                                <TextField
                                    label="Processing Fee (%)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.processingFee}
                                    onChange={(e) => handleFormChange("processingFee", e.target.value)}
                                    error={!!errors.processingFee}
                                    helperText={errors.processingFee}
                                />
                                <TextField
                                    label="Insurance (%)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.insurance}
                                    onChange={(e) => handleFormChange("insurance", e.target.value)}
                                    error={!!errors.insurance}
                                    helperText={errors.insurance}
                                />
                            </Stack>
                            {/* Min/Max Amount */}
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Min Amount"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.minAmount}
                                    onChange={(e) => handleFormChange("minAmount", e.target.value)}
                                    error={!!errors.minAmount}
                                    helperText={errors.minAmount}
                                />
                                <TextField
                                    label="Max Amount"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={form.maxAmount}
                                    onChange={(e) => handleFormChange("maxAmount", e.target.value)}
                                    error={!!errors.maxAmount}
                                    helperText={errors.maxAmount}
                                />
                            </Stack>
                            {/* Repayment Type */}
                            <FormControl fullWidth size="small">
                                <InputLabel>Repayment Type</InputLabel>
                                <Select
                                    value={form.repaymentType}
                                    onChange={(e) => handleFormChange("repaymentType", e.target.value)}
                                >
                                    <MenuItem value="Flat">Flat</MenuItem>
                                    <MenuItem value="Reducing">Reducing</MenuItem>
                                </Select>
                            </FormControl>
                            {/* Insurance Type */}
                            <FormControl fullWidth error={!!errors.insuranceType} size="small">
                                <InputLabel>Insurance Type</InputLabel>
                                <Select
                                    value={form.insuranceType}
                                    onChange={(e) => handleFormChange("insuranceType", e.target.value)}
                                >
                                    <MenuItem value="UP_FRONT">Up Front</MenuItem>
                                    <MenuItem value="DISTRIBUTED">Distributed</MenuItem>
                                </Select>
                                {errors.insuranceType && <FormHelperText>{errors.insuranceType}</FormHelperText>}
                            </FormControl>
                            {/* Terms & Conditions */}
                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <h6>Terms & Conditions</h6>
                                    <Stack direction="row" spacing={1}>
                                        {/* Hidden CSV file input */}
                                        <input
                                            type="file"
                                            accept=".csv"
                                            ref={csvInputRef}
                                            style={{ display: 'none' }}
                                            onChange={handleCsvFileChange}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Upload size={16} />}
                                            onClick={handleCsvUploadClick}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Upload CSV
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<FileSpreadsheet size={16} />}
                                            onClick={downloadCsvTemplate}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Template
                                        </Button>
                                        {form?.termsCondition?.length === 0 &&
                                            <button className="custom-button" onClick={handleAddTerm}>+ Add Term</button>
                                        }
                                    </Stack>
                                </Stack>

                                {form.termsCondition.length > 0 && (
                                    <Alert severity="info" sx={{ py: 0.5 }}>
                                        {form.termsCondition.length} term(s) configured
                                    </Alert>
                                )}

                                {form.termsCondition.map((term, index) => (
                                    <>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                label="Term Number"
                                                fullWidth
                                                size="small"
                                                value={term.termNumber}
                                                error={!!errors?.termsCondition?.[index]?.termNumber}
                                                helperText={errors?.termsCondition?.[index]?.termNumber || `Max 20 chars`}
                                                inputProps={{ maxLength: 20 }}
                                                onChange={(e) => handleTermChange(index, "termNumber", e.target.value)}
                                            />
                                            <TextField
                                                label="Effective Date"
                                                type="date"
                                                fullWidth
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                value={term.effectiveDate}
                                                error={!!errors?.termsCondition?.[index]?.effectiveDate}
                                                helperText={errors?.termsCondition?.[index]?.effectiveDate}
                                                onChange={(e) => handleTermChange(index, "effectiveDate", e.target.value)}
                                            />
                                        </Stack>

                                        <TextField
                                            label="Description"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            size="small"
                                            sx={{ mt: 1 }}
                                            error={!!errors?.termsCondition?.[index]?.description || term.description?.length > 255}
                                            helperText={
                                                errors?.termsCondition?.[index]?.description || 
                                                `${term.description?.length || 0}/255 characters`
                                            }
                                            inputProps={{ maxLength: 255 }}
                                            value={term.description}
                                            onChange={(e) => handleTermChange(index, "description", e.target.value)}
                                        />

                                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                            <Trash2 className="icon" size={20} onClick={() => handleRemoveTerm(index)} />
                                            {form?.termsCondition.length === index + 1 && (
                                                <button className="custom-button" onClick={handleAddTerm}>+ Add Term</button>
                                            )}
                                        </Stack>
                                    </>
                                ))}
                                {errors.termsCondition && (
                                    <FormHelperText error>{errors.termsCondition}</FormHelperText>
                                )}
                            </Stack>
                            {/* Checkboxes */}
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    control={<Checkbox checked={form.forExecutive} onChange={(e) => handleFormChange("forExecutive", e.target.checked)} />}
                                    label="For Executive"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={form.shariaFacility} onChange={(e) => handleFormChange("shariaFacility", e.target.checked)} />}
                                    label="Sharia Facility"
                                />
                            </Stack>
                        </Stack>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Save size={16} />}
                        onClick={handleSaveAsDraft}
                        disabled={saving}
                        sx={{ textTransform: 'none' }}
                    >
                        {editingDraftId ? 'Update Draft' : 'Save as Draft'}
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <button className="custom-button" onClick={handleClose} disabled={saving}>Cancel</button>
                        <button className="custom-button" onClick={handleSaveProduct} disabled={saving}>
                            {saving ? 'Saving...' : (editMode ? 'Update' : 'Create & Send to ESS')}
                        </button>
                    </Stack>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete product <strong>{selectedProduct?.name}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <button className="custom-button" onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Cancel</button>
                    <button 
                        className="custom-button" 
                        style={{ backgroundColor: '#d32f2f', color: 'white' }}
                        onClick={handleDeleteProduct} 
                        disabled={saving}
                    >
                        {saving ? 'Deleting...' : 'Delete'}
                    </button>
                </DialogActions>
            </Dialog>

            {/* CSV Preview Dialog */}
            <Dialog 
                open={csvDialogOpen} 
                onClose={handleCsvDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FileSpreadsheet size={24} />
                            <Typography variant="h6">Import Terms & Conditions</Typography>
                        </Stack>
                        <IconButton onClick={handleCsvDialogClose} size="small">
                            <X size={20} />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {csvFileName && (
                        <Chip 
                            label={csvFileName} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ mb: 2 }}
                        />
                    )}

                    {csvErrors.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {csvErrors.length} validation warning(s):
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {csvErrors.slice(0, 5).map((error, idx) => (
                                    <li key={idx}><Typography variant="body2">{error}</Typography></li>
                                ))}
                                {csvErrors.length > 5 && (
                                    <li><Typography variant="body2">...and {csvErrors.length - 5} more</Typography></li>
                                )}
                            </ul>
                        </Alert>
                    )}

                    {csvPreviewData.length > 0 ? (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Preview ({csvPreviewData.length} terms to import):
                            </Typography>
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Term Number</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Effective Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvPreviewData.map((term, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{term.termNumber}</TableCell>
                                                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {term.description}
                                                </TableCell>
                                                <TableCell>{term.effectiveDate}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {form.termsCondition.length > 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    You have {form.termsCondition.length} existing term(s). Choose to append or replace.
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Alert severity="error">
                            No valid data found in the CSV file. Please check the format and try again.
                            <br /><br />
                            Expected columns: <strong>TermsConditionNumber, Description, TCEffectiveDate</strong>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCsvDialogClose} color="inherit">
                        Cancel
                    </Button>
                    {csvPreviewData.length > 0 && (
                        <>
                            {form.termsCondition.length > 0 && (
                                <Button 
                                    onClick={handleCsvReplace} 
                                    variant="outlined"
                                    color="warning"
                                >
                                    Replace All ({csvPreviewData.length})
                                </Button>
                            )}
                            <Button 
                                onClick={handleCsvImportConfirm} 
                                variant="contained"
                                color="primary"
                            >
                                {form.termsCondition.length > 0 ? `Append (${csvPreviewData.length})` : `Import (${csvPreviewData.length})`}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Product Decommission Dialog */}
            <Dialog 
                open={decommissionDialogOpen} 
                onClose={() => !decommissioning && setDecommissionDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PowerOff size={24} color="#d32f2f" />
                        <Typography variant="h6">Decommission Products</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Decommissioning a product will notify ESS that these products are no longer available.
                        This action cannot be easily reversed.
                    </Alert>

                    <Typography variant="subtitle2" gutterBottom>
                        Select products to decommission:
                    </Typography>

                    {getActiveProducts().length === 0 ? (
                        <Alert severity="info">
                            No active products available for decommission.
                        </Alert>
                    ) : (
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    selectedForDecommission.length > 0 && 
                                                    selectedForDecommission.length < getActiveProducts().length
                                                }
                                                checked={
                                                    getActiveProducts().length > 0 &&
                                                    selectedForDecommission.length === getActiveProducts().length
                                                }
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedForDecommission(
                                                            getActiveProducts().map(p => p.productCode)
                                                        );
                                                    } else {
                                                        setSelectedForDecommission([]);
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>Product Code</TableCell>
                                        <TableCell>Product Name</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {getActiveProducts().map((product) => (
                                        <TableRow 
                                            key={product.productCode || product.id}
                                            hover
                                            onClick={() => handleToggleDecommissionSelect(product.productCode)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedForDecommission.includes(product.productCode)}
                                                    onChange={() => handleToggleDecommissionSelect(product.productCode)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {product.productCode}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{product.productName}</TableCell>
                                            <TableCell>
                                                <Chip label="Active" size="small" color="success" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {selectedForDecommission.length > 0 && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            <strong>{selectedForDecommission.length}</strong> product(s) selected for decommission:
                            <Box sx={{ mt: 1 }}>
                                {selectedForDecommission.map(code => (
                                    <Chip 
                                        key={code}
                                        label={code}
                                        size="small"
                                        onDelete={() => handleToggleDecommissionSelect(code)}
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ))}
                            </Box>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setDecommissionDialogOpen(false)}
                        disabled={decommissioning}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<PowerOff size={16} />}
                        onClick={handleDecommissionProducts}
                        disabled={decommissioning || selectedForDecommission.length === 0}
                    >
                        {decommissioning ? 'Decommissioning...' : `Decommission (${selectedForDecommission.length})`}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Product;
